import { Platform } from 'react-native';

import { darkBluePalette, lightPalette, midnightPurplePalette } from './palettes';
import { createTypePresets, fontSize, getBrandFontFamily } from './typography';

export const THEME_MODE = {
	LIGHT: 'light',
	DARK_BLUE: 'darkBlue',
	MIDNIGHT_PURPLE: 'midnightPurple',
};

export const THEME_MODE_OPTIONS = [
	{ label: 'Light', value: THEME_MODE.LIGHT },
	{ label: 'Dark Blue', value: THEME_MODE.DARK_BLUE },
	{ label: 'Onyx', value: THEME_MODE.MIDNIGHT_PURPLE },
];

const PALETTES = {
	[THEME_MODE.LIGHT]: lightPalette,
	[THEME_MODE.DARK_BLUE]: darkBluePalette,
	[THEME_MODE.MIDNIGHT_PURPLE]: midnightPurplePalette,
};

/**
 * Maps a saved or requested mode onto a supported one. The retired 'dark'
 * option became Onyx (saved as 'midnightPurple'); anything unknown falls back to the OS choice.
 */
export const normalizeThemeMode = (mode, colorScheme) => {
	if (mode === 'dark') {
		return THEME_MODE.MIDNIGHT_PURPLE;
	}
	if (PALETTES[mode]) {
		return mode;
	}
	return colorScheme === 'dark' ? THEME_MODE.DARK_BLUE : THEME_MODE.LIGHT;
};

// 4-pt grid; gutter is the screen side padding from the mockups.
export const space = {
	xxs: 2,
	xs: 4,
	sm: 8,
	md: 12,
	lg: 16,
	gutter: 18,
	xl: 24,
	xxl: 32,
};

export const radius = {
	tag: 6,
	chip: 8,
	control: 10,
	card: 12,
	hero: 14,
	sheet: 22,
	pill: 999,
};

export const touch = {
	min: 44,
	compactButton: 48,
	primaryButton: 52,
	row: 52,
	rowWithSubtitle: 68,
	currentRow: 76,
};

export const layout = {
	maxContentWidth: 600,
	headerHeight: 56,
	tabBarHeight: 68,
};

const createElevation = (isDark) => ({
	none: {},
	// Floating layers (sheets, dialogs, toasts) only; cards stay flat.
	floating: Platform.select({
		android: { elevation: 8 },
		default: {
			shadowColor: 'black',
			shadowOpacity: isDark ? 0.4 : 0.16,
			shadowRadius: 12,
			shadowOffset: { width: 0, height: 4 },
		},
	}),
});

/**
 * Builds theme.ui for a mode: colors, type presets, fontSize, fontFamily(weight),
 * areBrandFontsLoaded, space, radius, touch, layout, elevation, isDark and
 * statusBarStyle.
 */
export const getUiTheme = (mode, { areBrandFontsLoaded = false } = {}) => {
	const resolvedMode = PALETTES[mode] ? mode : THEME_MODE.LIGHT;
	const isDark = resolvedMode !== THEME_MODE.LIGHT;

	return {
		mode: resolvedMode,
		isDark,
		statusBarStyle: isDark ? 'light-content' : 'dark-content',
		colors: PALETTES[resolvedMode],
		type: createTypePresets(areBrandFontsLoaded),
		fontSize,
		areBrandFontsLoaded,
		fontFamily: fontWeight => getBrandFontFamily(fontWeight, areBrandFontsLoaded),
		space,
		radius,
		touch,
		layout,
		elevation: createElevation(isDark),
	};
};
